import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Cabecalho from '../cabecalho/Cabecalho';
import Rodape from '../rodape/Rodape';
import Menu from '../menu/Menu';
import Titulo from '../titulo/Titulo';
import ComunicadosService from '../../service/comunicados/ComunicadosService';
import RegiaoService from '../../service/regioes';
import PontosService from '../../service/pontos';

// =============================================================================
// PAGINA: FORMULARIO DE COMUNICADO (criar/editar)
// =============================================================================
// FormBuilder com preview ao vivo. Suporta 4 tipos de popup:
//   - informativo
//   - sim_nao
//   - multipla_escolha
//   - texto_livre
// =============================================================================

const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: #fff;
`;

const MainContent = styled.main`
    padding-top: 85px;
    padding-left: 260px;
    padding-right: 24px;
    padding-bottom: 60px;
    box-sizing: border-box;
    flex: 1 0 auto;
    @media (max-width: 1200px) and (min-width: 768px) {
        padding-left: 80px;
    }
    @media (max-width: 767px) {
        padding-left: 16px;
        padding-right: 16px;
        padding-bottom: 16px;
    }
`;

const Container = styled.div`
    padding: 24px;
    max-width: 1200px;
    margin: 0 auto;
    font-family: 'Roboto', sans-serif;
`;

const SplitLayout = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const FormSection = styled.div`
    background: white;
    padding: 24px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
`;

const PreviewSection = styled.div`
    background: #f8f9fa;
    padding: 24px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    position: sticky;
    top: 100px;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
`;

const Field = styled.div`
    margin-bottom: 20px;
`;

const Label = styled.label`
    display: block;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
    font-size: 14px;
`;

const Input = styled.input`
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #008080;
    border-radius: 4px;
    font-size: 14px;
    color: #333;
    outline: none;
    box-sizing: border-box;
    &:focus { box-shadow: 0 0 0 2px rgba(0, 128, 128, 0.2); }
`;

const TextArea = styled.textarea`
    width: 100%;
    min-height: 80px;
    padding: 10px 14px;
    border: 1px solid #008080;
    border-radius: 4px;
    font-size: 14px;
    color: #333;
    outline: none;
    box-sizing: border-box;
    resize: vertical;
    font-family: inherit;
    &:focus { box-shadow: 0 0 0 2px rgba(0, 128, 128, 0.2); }
`;

const Select = styled.select`
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #008080;
    border-radius: 4px;
    font-size: 14px;
    color: #008080;
    background: white;
    outline: none;
    cursor: pointer;
`;

const CheckboxContainer = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 10px;
    background: #fff8e1;
    border-radius: 4px;
    border: 1px solid #ffd54f;
`;

const PrimaryButton = styled.button`
    background: #008080;
    border: none;
    color: #fff;
    padding: 12px 24px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    margin-right: 8px;
    &:hover { background: #006666; }
    &:disabled { background: #ccc; cursor: not-allowed; }
`;

const SecondaryButton = styled.button`
    background: white;
    border: 1px solid #008080;
    color: #008080;
    padding: 12px 24px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    &:hover { background: #f0fdfd; }
`;

const OptionChip = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    align-items: center;
`;

const RemoveChipButton = styled.button`
    background: #fde8e8;
    border: 1px solid #d32f2f;
    color: #d32f2f;
    width: 32px;
    height: 32px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: bold;
`;

const AddChipButton = styled.button`
    background: #e0f2f1;
    border: 1px dashed #008080;
    color: #008080;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
`;

// ===== PREVIEW STYLES =====

const PreviewCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    max-width: 420px;
    margin: 0 auto;
`;

const PreviewTitle = styled.h3`
    font-size: 18px;
    font-weight: 700;
    color: ${(props) => props.$cor || '#333'};
    margin: 0 0 12px 0;
`;

const PreviewText = styled.p`
    color: #555;
    font-size: 14px;
    line-height: 1.5;
    margin: 0 0 16px 0;
`;

const PreviewImage = styled.img`
    width: 100%;
    height: 180px;
    object-fit: cover;
    border-radius: 8px;
    margin-bottom: 12px;
`;

const PreviewButton = styled.button`
    background: ${(props) => props.$cor || '#008080'};
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    font-size: 14px;
    margin-right: 8px;
    &:last-child { margin-right: 0; }
`;

const PreviewTextButton = styled.button`
    background: transparent;
    color: ${(props) => props.$cor || '#008080'};
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    font-size: 14px;
    margin-right: 8px;
`;

const PreviewLabel = styled.div`
    text-align: center;
    color: #999;
    font-size: 12px;
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
`;

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

const FormComunicado = () => {
    const navigate = useNavigate();
    const userId = typeof window !== 'undefined' ? localStorage.getItem('idUsuario') : null;

    // Estado do formulario
    const [tipoPopup, setTipoPopup] = useState('informativo');
    const [titulo, setTitulo] = useState('');
    const [mensagem, setMensagem] = useState('');
    const [imagemUrl, setImagemUrl] = useState('');
    const [obrigatorio, setObrigatorio] = useState(false);
    const [opcoes, setOpcoes] = useState(['Sim', 'Não']);
    const [textoPlaceholder, setTextoPlaceholder] = useState('Digite sua resposta...');
    const [textoSim, setTextoSim] = useState('Sim');
    const [textoNao, setTextoNao] = useState('Não');
    const [filtroAlvo, setFiltroAlvo] = useState('todos');
    const [alvoId, setAlvoId] = useState('');

    const [regioes, setRegioes] = useState([]);
    const [pontos, setPontos] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState(null);

    useEffect(() => {
        Promise.all([
            RegiaoService.getRegioes().catch(() => []),
            PontosService.getPontos().catch(() => [])
        ]).then(([r, p]) => {
            setRegioes(Array.isArray(r) ? r : (r?.regioes || []));
            setPontos(Array.isArray(p) ? p : (p?.pontos || []));
        });
    }, []);

    const adicionarOpcao = () => setOpcoes([...opcoes, '']);
    const removerOpcao = (idx) => setOpcoes(opcoes.filter((_, i) => i !== idx));
    const atualizarOpcao = (idx, valor) => {
        const novas = [...opcoes];
        novas[idx] = valor;
        setOpcoes(novas);
    };

    const handleSalvar = async (dispararDepois) => {
        // Validacao
        if (!titulo.trim()) { setErro('Titulo obrigatorio'); return; }
        if (!mensagem.trim()) { setErro('Mensagem obrigatoria'); return; }
        if (tipoPopup === 'multipla_escolha') {
            const opcoesValidas = opcoes.filter(o => o.trim());
            if (opcoesValidas.length < 2) {
                setErro('Minimo 2 opcoes para enquete');
                return;
            }
        }
        if (filtroAlvo !== 'todos' && !alvoId) {
            setErro('Selecione o alvo especifico');
            return;
        }

        setEnviando(true);
        setErro(null);

        try {
            const idCampanha = `camp_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

            const dados = {
                idCampanha,
                tipoPopup,
                titulo,
                mensagem,
                imagemUrl: tipoPopup === 'informativo' && imagemUrl.trim() ? imagemUrl.trim() : null,
                obrigatorio,
                opcoes: tipoPopup === 'multipla_escolha'
                    ? opcoes.filter(o => o.trim())
                    : (tipoPopup === 'sim_nao' ? [] : []),
                textoPlaceholder,
                textoSim: tipoPopup === 'sim_nao' ? textoSim : 'Sim',
                textoNao: tipoPopup === 'sim_nao' ? textoNao : 'Não',
                filtro: filtroAlvo,
                alvoId: alvoId || null,
            };

            const resposta = await ComunicadosService.criar(dados);
            const novoId = resposta?.comunicado?._id || resposta?._id;

            if (dispararDepois && novoId) {
                const disparo = await ComunicadosService.disparar(novoId);
                alert(`Comunicado criado e enviado para ${disparo.sucessos} dispositivo(s)!`);
            } else if (novoId) {
                alert('Comunicado criado! Use "Disparar" na lista para enviar.');
            }

            navigate('/comunicacoes-especiais');
        } catch (err) {
            console.error('Erro ao criar comunicado:', err);
            setErro('Erro ao criar comunicado. Verifique os dados e tente novamente.');
        } finally {
            setEnviando(false);
        }
    };

    // ===== PREVIEW =====
    const renderPreview = () => {
        const tituloCor = tipoPopup === 'informativo' ? '#0288d1' : '#008080';
        const isUrgente = obrigatorio;

        return (
            <div>
                <PreviewLabel>Preview no celular</PreviewLabel>
                <PreviewCard style={isUrgente ? { borderTop: '4px solid #d32f2f' } : {}}>
                    <PreviewTitle $cor={isUrgente ? '#d32f2f' : tituloCor}>
                        {titulo || 'Titulo do popup'}
                    </PreviewTitle>

                    {tipoPopup === 'informativo' && imagemUrl && (
                        <PreviewImage src={imagemUrl} alt="imagem" onError={(e) => e.target.style.display = 'none'} />
                    )}

                    <PreviewText>{mensagem || 'Sua mensagem aparecera aqui.'}</PreviewText>

                    {tipoPopup === 'informativo' && (
                        <div style={{ textAlign: 'right' }}>
                            <PreviewButton $cor="#008080">Entendi</PreviewButton>
                        </div>
                    )}

                    {tipoPopup === 'sim_nao' && (
                        <div style={{ textAlign: 'right' }}>
                            <PreviewTextButton $cor="#d32f2f">{textoNao}</PreviewTextButton>
                            <PreviewButton $cor="#008080">{textoSim}</PreviewButton>
                        </div>
                    )}

                    {tipoPopup === 'multipla_escolha' && (
                        <div>
                            {opcoes.filter(o => o.trim()).map((op, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 14 }}>
                                    <input type="radio" name="preview-op" disabled />
                                    <span>{op}</span>
                                </div>
                            ))}
                            <div style={{ textAlign: 'right', marginTop: 8 }}>
                                <PreviewButton $cor="#008080">Enviar</PreviewButton>
                            </div>
                        </div>
                    )}

                    {tipoPopup === 'texto_livre' && (
                        <div>
                            <div style={{ width: '100%', padding: 10, border: '1px solid #ccc', borderRadius: 8, color: '#999', fontSize: 13, minHeight: 60 }}>
                                {textoPlaceholder}
                            </div>
                            <div style={{ textAlign: 'right', marginTop: 8 }}>
                                <PreviewButton $cor="#008080">Enviar</PreviewButton>
                            </div>
                        </div>
                    )}
                </PreviewCard>
                {isUrgente && (
                    <p style={{ textAlign: 'center', color: '#d32f2f', marginTop: 12, fontSize: 13, fontWeight: 600 }}>
                        ⚠️ Pop-up OBRIGATORIO: usuario nao pode fechar sem responder
                    </p>
                )}
            </div>
        );
    };

    return (
        <PageWrapper>
            <Menu />
            <Cabecalho />
            <MainContent>
                <Container>
                    <Titulo>Criar Comunicado Dinâmico</Titulo>

                    {erro && (
                        <div style={{ background: '#fde8e8', color: '#c62828', padding: 12, borderRadius: 6, marginBottom: 16 }}>
                            {erro}
                        </div>
                    )}

                    <SplitLayout>
                        {/* FORM */}
                        <FormSection>
                            <h2 style={{ color: '#008080', marginTop: 0, marginBottom: 20 }}>Configuração</h2>

                            <Field>
                                <Label>Tipo de Popup *</Label>
                                <Select value={tipoPopup} onChange={(e) => setTipoPopup(e.target.value)}>
                                    <option value="informativo">📢 Informativo (apenas leitura)</option>
                                    <option value="sim_nao">✅ Sim/Não (decisão rapida)</option>
                                    <option value="multipla_escolha">📊 Enquete (multipla escolha)</option>
                                    <option value="texto_livre">✍️ Texto livre (justificativa)</option>
                                </Select>
                            </Field>

                            <Field>
                                <Label>Titulo *</Label>
                                <Input
                                    type="text"
                                    value={titulo}
                                    onChange={(e) => setTitulo(e.target.value)}
                                    placeholder="Ex: Como foi o ultimo evento?"
                                    maxLength={100}
                                />
                            </Field>

                            <Field>
                                <Label>Mensagem *</Label>
                                <TextArea
                                    value={mensagem}
                                    onChange={(e) => setMensagem(e.target.value)}
                                    placeholder="Descreva o comunicado..."
                                    maxLength={1000}
                                />
                            </Field>

                            {tipoPopup === 'informativo' && (
                                <Field>
                                    <Label>URL da imagem (opcional)</Label>
                                    <Input
                                        type="text"
                                        value={imagemUrl}
                                        onChange={(e) => setImagemUrl(e.target.value)}
                                        placeholder="https://exemplo.com/imagem.jpg"
                                    />
                                </Field>
                            )}

                            {tipoPopup === 'sim_nao' && (
                                <>
                                    <Field>
                                        <Label>Texto do botão "Sim"</Label>
                                        <Input type="text" value={textoSim} onChange={(e) => setTextoSim(e.target.value)} />
                                    </Field>
                                    <Field>
                                        <Label>Texto do botão "Não"</Label>
                                        <Input type="text" value={textoNao} onChange={(e) => setTextoNao(e.target.value)} />
                                    </Field>
                                </>
                            )}

                            {tipoPopup === 'multipla_escolha' && (
                                <Field>
                                    <Label>Opções (minimo 2, maximo 10)</Label>
                                    {opcoes.map((op, idx) => (
                                        <OptionChip key={idx}>
                                            <Input
                                                type="text"
                                                value={op}
                                                onChange={(e) => atualizarOpcao(idx, e.target.value)}
                                                placeholder={`Opção ${idx + 1}`}
                                            />
                                            {opcoes.length > 2 && (
                                                <RemoveChipButton onClick={() => removerOpcao(idx)}>×</RemoveChipButton>
                                            )}
                                        </OptionChip>
                                    ))}
                                    {opcoes.length < 10 && (
                                        <AddChipButton onClick={adicionarOpcao}>+ Adicionar opção</AddChipButton>
                                    )}
                                </Field>
                            )}

                            {tipoPopup === 'texto_livre' && (
                                <Field>
                                    <Label>Placeholder do campo</Label>
                                    <Input
                                        type="text"
                                        value={textoPlaceholder}
                                        onChange={(e) => setTextoPlaceholder(e.target.value)}
                                    />
                                </Field>
                            )}

                            <Field>
                                <CheckboxContainer>
                                    <input
                                        type="checkbox"
                                        checked={obrigatorio}
                                        onChange={(e) => setObrigatorio(e.target.checked)}
                                        style={{ width: 18, height: 18, accentColor: '#d32f2f' }}
                                    />
                                    <span style={{ fontWeight: 600, color: '#333' }}>
                                        🔒 Pop-up OBRIGATORIO (usuario não pode fechar sem responder)
                                    </span>
                                </CheckboxContainer>
                            </Field>

                            <Field>
                                <Label>Para quem enviar</Label>
                                <Select value={filtroAlvo} onChange={(e) => { setFiltroAlvo(e.target.value); setAlvoId(''); }}>
                                    <option value="todos">Todos os publicadores</option>
                                    <option value="regiao">Por região</option>
                                    <option value="ponto">Por ponto especifico</option>
                                </Select>
                            </Field>

                            {filtroAlvo === 'regiao' && (
                                <Field>
                                    <Label>Selecione a região</Label>
                                    <Select value={alvoId} onChange={(e) => setAlvoId(e.target.value)}>
                                        <option value="">-- Escolha --</option>
                                        {regioes.map(r => <option key={r._id} value={r._id}>{r.nome}</option>)}
                                    </Select>
                                </Field>
                            )}

                            {filtroAlvo === 'ponto' && (
                                <Field>
                                    <Label>Selecione o ponto</Label>
                                    <Select value={alvoId} onChange={(e) => setAlvoId(e.target.value)}>
                                        <option value="">-- Escolha --</option>
                                        {pontos.map(p => <option key={p._id} value={p._id}>{p.nome}</option>)}
                                    </Select>
                                </Field>
                            )}

                            <div style={{ display: 'flex', marginTop: 24, gap: 8, flexWrap: 'wrap' }}>
                                <PrimaryButton onClick={() => handleSalvar(false)} disabled={enviando}>
                                    {enviando ? 'Salvando...' : '💾 Salvar e Disparar depois'}
                                </PrimaryButton>
                                <SecondaryButton onClick={() => navigate('/comunicacoes-especiais')} disabled={enviando}>
                                    Cancelar
                                </SecondaryButton>
                            </div>
                        </FormSection>

                        {/* PREVIEW */}
                        <PreviewSection>
                            <h3 style={{ color: '#008080', marginTop: 0 }}>👁️ Preview ao vivo</h3>
                            {renderPreview()}
                        </PreviewSection>
                    </SplitLayout>
                </Container>
            </MainContent>
            <Rodape />
        </PageWrapper>
    );
};

export default FormComunicado;
